import {ReportStatus} from './_models'

export const normalizePositionReportStatus = (status?: string | null): ReportStatus =>
  String(status ?? '').trim().toUpperCase() === ReportStatus.SUBMITTED
    ? ReportStatus.SUBMITTED
    : ReportStatus.DRAFT

export const normalizeCrewBaseRank = (rankName?: string | null): string => {
  const normalized = String(rankName ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*-\s*\d+\s*$/, '')
    .toUpperCase()

  if (!normalized) return ''
  if (normalized.includes('MASTER') || normalized.includes('CAPTAIN')) {
    return 'MASTER (CAPTAIN)'
  }
  return normalized
}

export const isMasterCaptainRank = (rankName?: string | null): boolean =>
  normalizeCrewBaseRank(rankName) === 'MASTER (CAPTAIN)'

export const isMasterOrChiefEngineerUser = (currentUser: any): boolean => {
  if (!currentUser) return false

  const rankStr = String(
    currentUser?.rank?.rank ??
      currentUser?.rankName ??
      currentUser?.crew?.rank?.rank ??
      currentUser?.crew?.rank ??
      currentUser?.rank ??
      currentUser?.role?.roleName ??
      currentUser?.roleName ??
      ''
  ).toUpperCase()

  const roleStr = String(
    currentUser?.role?.roleName ??
      currentUser?.role?.roleType ??
      currentUser?.effectiveBehaviorType ??
      ''
  ).toUpperCase()

  const isMaster =
    rankStr.includes('MASTER') ||
    rankStr.includes('CAPTAIN') ||
    roleStr.includes('MASTER') ||
    roleStr.includes('CAPTAIN')

  const isChiefEngineer =
    rankStr.includes('CHIEF ENGINEER') ||
    rankStr.includes('CHIEF ENGINEERING OFFICER') ||
    rankStr.includes('C/E') ||
    rankStr.includes('CHIEF ENGR') ||
    rankStr.includes('CHIEF ENG') ||
    roleStr.includes('CHIEF ENGINEER') ||
    roleStr.includes('CHIEF ENGINEERING OFFICER')

  return isMaster || isChiefEngineer
}

export const isCompanyLoginUser = (currentUser: any): boolean => {
  if (!currentUser) return false
  const behavior = String(
    currentUser?.tenant?.effectiveBehaviorType ??
      currentUser?.effectiveBehaviorType ??
      currentUser?.role?.effectiveBehaviorType ??
      currentUser?.role?.roleType ??
      currentUser?.role?.roleName ??
      ''
  ).toUpperCase()

  const roleId = Number(currentUser?.role?.id ?? 0)
  return behavior !== 'CREW' || roleId === 1 || roleId === 2 || roleId === 99
}

export type PositionReportActionState = {
  canSaveDraft: boolean
  canSubmit: boolean
  isLocked: boolean
  isMasterOrChiefEngineer: boolean
  isCompanyUser: boolean
  primaryDraftLabel: 'Save Draft' | 'Save Changes'
  normalizedStatus: ReportStatus
  submitTooltip?: string
}

export const getPositionReportActionState = ({
  status,
  canWriteReport,
  isEditMode,
  isCrew,
  currentUser,
}: {
  status?: string | null
  canWriteReport: boolean
  isEditMode: boolean
  isCrew?: boolean
  currentUser?: any
}): PositionReportActionState => {
  const normalizedStatus = normalizePositionReportStatus(status)
  const isCompanyUser = isCompanyLoginUser(currentUser) || !isCrew
  const isMasterOrChiefEng = isMasterOrChiefEngineerUser(currentUser)

  const isSubmitted = normalizedStatus === ReportStatus.SUBMITTED
  const isLocked = isSubmitted && !isCompanyUser

  // All ranks can save draft on DRAFT reports
  const canSaveDraft =
    canWriteReport &&
    !isLocked &&
    (normalizedStatus === ReportStatus.DRAFT || (isEditMode && isCompanyUser))

  // Master, Chief Engineer, and Company Users can Submit & Lock DRAFT reports
  const canSubmit =
    canWriteReport &&
    normalizedStatus === ReportStatus.DRAFT &&
    (isMasterOrChiefEng || isCompanyUser)

  const submitTooltip =
    !isMasterOrChiefEng && !isCompanyUser && isCrew
      ? 'Draft created. Only Master or Chief Engineer can Submit & Lock this report.'
      : undefined

  return {
    canSaveDraft,
    canSubmit,
    isLocked,
    isMasterOrChiefEngineer: isMasterOrChiefEng,
    isCompanyUser,
    primaryDraftLabel: normalizedStatus === ReportStatus.DRAFT ? 'Save Draft' : 'Save Changes',
    normalizedStatus,
    submitTooltip,
  }
}
